'use client';

import { createContext, useContext, useRef, type ReactNode } from 'react';
import { useStore } from 'zustand';

import { AppStoreType, createAppBoundStore } from './store';

export type AppStoreApi = ReturnType<typeof createAppBoundStore>;

export const AppStoreContext = createContext<AppStoreApi | undefined>(
  undefined
);

export interface AppProviderProps {
  children: ReactNode;
  storeOverride?: AppStoreApi;
}

// Setup following docs recommendation https://zustand.docs.pmnd.rs/guides/nextjs
export const AppStoreProvider = ({
  children,
  storeOverride,
}: AppProviderProps) => {
  const storeRef = useRef<AppStoreApi | null>(null);

  if (storeRef.current === null) {
    storeRef.current = storeOverride ?? createAppBoundStore();
  }

  return (
    <AppStoreContext.Provider value={storeRef.current}>
      {children}
    </AppStoreContext.Provider>
  );
};

// we already have inline selectors exported from store. Idk if we need this.
// Use for inline selectors (e.g., s => s.cart) to subscribe to specific state slices.
export function useAppStore<T>(selector: (store: AppStoreType) => T): T {
  const appStoreContext = useContext(AppStoreContext);

  if (!appStoreContext) {
    throw new Error(`useAppStore must be used within AppProvider`);
  }

  return useStore(appStoreContext, selector);
}

// Use for autogenerated selectors with autocomplete (e.g., .use.cart()) or accessing actions via getState().
export function useAppStoreInContext(): AppStoreApi {
  const store = useContext(AppStoreContext);
  if (!store) {
    throw new Error('useAppStoreContext must be used within AppStoreProvider');
  }
  return store;
}
